import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  LinearProgress,
  Chip,
  IconButton,
  Tabs,
  Tab
} from '@mui/material';
import {
  Upload as UploadIcon,
  CloudUpload as CloudUploadIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RetryIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Papa from 'papaparse';

import apiClient from '../../../../../utils/apiClient';

const JOB_TYPES = {
  standard_processing: {
    label: 'Standard NCES Processing',
    description: 'Process existing NCES schools through all Cloud Run services',
    requiredFields: ['NCES_ID', 'Team_Name'],
    exampleData: [
      { NCES_ID: '123456789012', Team_Name: 'Elementary Team' },
      { NCES_ID: '098765432109', Team_Name: 'Middle School Team' }
    ]
  },
  reprocessing: {
    label: 'School Reprocessing',
    description: 'Reprocess existing schools with updated data or services',
    requiredFields: ['School_Identifier', 'Services'],
    exampleData: [
      { School_Identifier: '123456789012', Services: 'esri,metrics', Updated_Address: '123 Main St, City, ST 12345' },
      { School_Identifier: 'uuid-example-1234', Services: 'projections,nearby_schools', Updated_Latitude: '40.7128', Updated_Longitude: '-74.0060' }
    ]
  },
  new_schools: {
    label: 'Drop a Pin (New Locations)',
    description: 'Create new data points at any location',
    requiredFields: ['Name', 'Street', 'City', 'State', 'Zip', 'Team_ID'],
    exampleData: [
      { Name: 'Example Elementary', Street: '123 School St', City: 'Anytown', State: 'ST', Zip: '12345', Team_ID: 'team_123' },
      { Name: 'Sample Middle School', Street: '456 Education Ave', City: 'Somewhere', State: 'ST', Zip: '67890', Team_ID: 'team_456' }
    ]
  }
};

const JOB_STATUS_COLORS = {
  created: 'info',
  processing: 'primary',
  completed: 'success',
  failed: 'error',
  cancelled: 'warning'
};

/** Maximum schools per bulk job (must match backend MAX_SCHOOLS_PER_JOB) */
const MAX_SCHOOLS_PER_JOB = 1000;

const BulkProcessingWorkflow = () => {
  const { enqueueSnackbar } = useSnackbar();
  
  // Job configuration
  const [jobType, setJobType] = useState('standard_processing');
  const [chunkSize, setChunkSize] = useState(5);
  const [forceReprocess, setForceReprocess] = useState(false);
  const [createTeamsIfMissing, setCreateTeamsIfMissing] = useState(true);
  const [runAiSummaries, setRunAiSummaries] = useState(false);
  
  // File upload and data
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [manualData, setManualData] = useState('');
  const [uploadMethod, setUploadMethod] = useState('csv'); // 'csv' or 'manual'
  
  // Job management
  const [currentJob, setCurrentJob] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [aiSummariesJobStatus, setAiSummariesJobStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // UI state
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [validationErrors, setValidationErrors] = useState([]);

  const handleFileUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      enqueueSnackbar('Please upload a CSV file', { variant: 'error' });
      return;
    }

    setCsvFile(file);
    
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          enqueueSnackbar('CSV parsing errors detected', { variant: 'warning' });
          console.warn('CSV parsing errors:', results.errors);
        }
        
        setCsvData(results.data);
        setValidationErrors([]);
        
        // Validate required fields
        const requiredFields = JOB_TYPES[jobType].requiredFields;
        const errors = [];
        
        results.data.forEach((row, index) => {
          requiredFields.forEach(field => {
            if (!row[field] || row[field].trim() === '') {
              errors.push({
                row: index + 1,
                field,
                message: `Missing required field: ${field}`
              });
            }
          });
        });
        
        setValidationErrors(errors);
        
        if (errors.length === 0) {
          enqueueSnackbar(`Successfully loaded ${results.data.length} schools`, { variant: 'success' });
        } else {
          enqueueSnackbar(`Loaded ${results.data.length} schools with ${errors.length} validation errors`, { variant: 'warning' });
        }
      },
      error: (error) => {
        enqueueSnackbar(`Error parsing CSV: ${error.message}`, { variant: 'error' });
      }
    });
  }, [jobType, enqueueSnackbar]);

  const handleManualDataChange = useCallback((event) => {
    const text = event.target.value;
    setManualData(text);
    
    if (text.trim()) {
      try {
        // Try to parse as CSV
        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            setCsvData(results.data);
            
            // Validate required fields
            const requiredFields = JOB_TYPES[jobType].requiredFields;
            const errors = [];
            
            results.data.forEach((row, index) => {
              requiredFields.forEach(field => {
                if (!row[field] || row[field].trim() === '') {
                  errors.push({
                    row: index + 1,
                    field,
                    message: `Missing required field: ${field}`
                  });
                }
              });
            });
            
            setValidationErrors(errors);
          }
        });
      } catch (error) {
        setValidationErrors([{ row: 0, field: 'parsing', message: 'Invalid CSV format' }]);
      }
    } else {
      setCsvData([]);
      setValidationErrors([]);
    }
  }, [jobType]);

  const downloadTemplate = useCallback(async () => {
    try {
      const response = await apiClient.get(`/v2/bulk/templates/csv?type=${jobType}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bulk_${jobType}_template.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('Template downloaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error downloading template:', error);
      enqueueSnackbar('Error downloading template', { variant: 'error' });
    }
  }, [jobType, enqueueSnackbar]);

  const startStatusPolling = useCallback((jobId) => {
    setAiSummariesJobStatus(null);
    const pollStatus = async () => {
      try {
        const response = await apiClient.get(`/v2/bulk/jobs/${jobId}/status`);
        setJobStatus(response.data);
        const data = response.data;

        // If main job has a linked AI summaries job, fetch its status (separate progress)
        let aiStatus = null;
        if (data.ai_summaries_job_id) {
          try {
            const aiResponse = await apiClient.get(`/v2/bulk/jobs/${data.ai_summaries_job_id}/status`);
            aiStatus = aiResponse.data;
            setAiSummariesJobStatus(aiStatus);
          } catch (e) {
            console.warn('Error fetching AI summaries job status:', e);
          }
        }

        // Keep polling if main job or AI summaries job is still active
        const mainActive = ['created', 'processing'].includes(data.status);
        const aiActive = aiStatus && ['created', 'processing'].includes(aiStatus.status);
        if (mainActive || aiActive) {
          setTimeout(pollStatus, 2000);
        }
      } catch (error) {
        console.error('Error polling job status:', error);
      }
    };

    pollStatus();
  }, []);

  const submitBulkJob = useCallback(async () => {
    if (csvData.length === 0) {
      enqueueSnackbar('Please upload schools data first', { variant: 'error' });
      return;
    }

    if (csvData.length > MAX_SCHOOLS_PER_JOB) {
      enqueueSnackbar(`Maximum ${MAX_SCHOOLS_PER_JOB} schools per job. Split your file or reduce the number of rows.`, { variant: 'error' });
      return;
    }

    if (validationErrors.length > 0) {
      enqueueSnackbar('Please fix validation errors before submitting', { variant: 'error' });
      return;
    }

    setIsSubmitting(true);

    try {
      // Transform CSV data to API format
      let schoolsData;
      
      if (jobType === 'standard_processing') {
        schoolsData = csvData.map(row => ({
          nces_id: row.NCES_ID?.trim(),
          team_name: row.Team_Name?.trim()
        }));
      } else if (jobType === 'reprocessing') {
        schoolsData = csvData.map(row => ({
          school_identifier: row.School_Identifier?.trim(),
          services: row.Services?.split(',').map(s => s.trim()).filter(Boolean),
          updated_address: row.Updated_Address?.trim() || undefined,
          updated_coordinates: (row.Updated_Latitude && row.Updated_Longitude) ? {
            latitude: parseFloat(row.Updated_Latitude),
            longitude: parseFloat(row.Updated_Longitude)
          } : undefined
        }));
      } else if (jobType === 'new_schools') {
        schoolsData = csvData.map(row => ({
          name: row.Name?.trim(),
          address: {
            street: row.Street?.trim(),
            city: row.City?.trim(),
            state: row.State?.trim(),
            zip: row.Zip?.trim()
          },
          team_id: row.Team_ID?.trim()
        }));
      }

      const response = await apiClient.post('/v2/bulk/process', {
        job_type: jobType,
        schools: schoolsData,
        options: {
          chunk_size: chunkSize,
          force_reprocess: forceReprocess,
          create_teams_if_missing: createTeamsIfMissing,
          run_ai_summaries: jobType === 'standard_processing' ? runAiSummaries : false
        }
      });

      setCurrentJob(response.data);
      setTabValue(1); // Switch to monitoring tab
      enqueueSnackbar(`Bulk job created: ${response.data.job_id}`, { variant: 'success' });
      
      // Start polling for status
      // eslint-disable-next-line react-hooks/exhaustive-deps
      startStatusPolling(response.data.job_id);

    } catch (error) {
      console.error('Error submitting bulk job:', error);
      enqueueSnackbar(`Error submitting job: ${error.response?.data?.error || error.message}`, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  }, [csvData, validationErrors, jobType, chunkSize, forceReprocess, createTeamsIfMissing, runAiSummaries, enqueueSnackbar, startStatusPolling]);

  const cancelJob = useCallback(async () => {
    if (!currentJob) return;
    
    try {
      await apiClient.post(`/v2/bulk/jobs/${currentJob.job_id}/cancel`);
      enqueueSnackbar('Job cancelled successfully', { variant: 'success' });
      
      // Refresh status
      const response = await apiClient.get(`/v2/bulk/jobs/${currentJob.job_id}/status`);
      setJobStatus(response.data);
    } catch (error) {
      console.error('Error cancelling job:', error);
      enqueueSnackbar('Error cancelling job', { variant: 'error' });
    }
  }, [currentJob, enqueueSnackbar]);

  const retryFailedSchools = useCallback(async () => {
    if (!currentJob) return;
    
    try {
      await apiClient.post(`/v2/bulk/jobs/${currentJob.job_id}/retry-failed`);
      enqueueSnackbar('Retrying failed schools...', { variant: 'info' });
      
      // Resume status polling
      startStatusPolling(currentJob.job_id);
    } catch (error) {
      console.error('Error retrying failed schools:', error);
      enqueueSnackbar('Error retrying failed schools', { variant: 'error' });
    }
  }, [currentJob, enqueueSnackbar, startStatusPolling]);

  const renderUploadSection = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          📤 Upload Schools Data
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Processing Type</InputLabel>
            <Select
              value={jobType}
              onChange={(e) => setJobType(e.target.value)}
              label="Processing Type"
            >
              {Object.entries(JOB_TYPES).map(([key, config]) => (
                <MenuItem key={key} value={key}>
                  <Box>
                    <Typography variant="body1">{config.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {config.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Tabs value={uploadMethod} onChange={(e, value) => setUploadMethod(value)}>
          <Tab label="CSV Upload" value="csv" icon={<UploadIcon />} />
          <Tab label="Manual Entry" value="manual" icon={<TextField />} />
        </Tabs>

        <Box sx={{ mt: 2 }}>
          {uploadMethod === 'csv' ? (
            <Box>
              <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={downloadTemplate}
                >
                  Download Template
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Required fields: {JOB_TYPES[jobType].requiredFields.join(', ')}
                </Typography>
              </Box>
              
              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUploadIcon />}
                fullWidth
                sx={{ mb: 2 }}
              >
                Choose CSV File
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleFileUpload}
                />
              </Button>
              
              {csvFile && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  File: {csvFile.name} ({csvData.length} rows)
                </Alert>
              )}
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Paste CSV data with headers: {JOB_TYPES[jobType].requiredFields.join(', ')}
              </Typography>
              <TextField
                multiline
                rows={8}
                fullWidth
                placeholder={`Example:\n${Object.keys(JOB_TYPES[jobType].exampleData[0]).join(',')}\n${JOB_TYPES[jobType].exampleData.map(row => Object.values(row).join(',')).join('\n')}`}
                value={manualData}
                onChange={handleManualDataChange}
                sx={{ mb: 2 }}
              />
            </Box>
          )}
        </Box>

        {validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Validation Errors ({validationErrors.length}):
            </Typography>
            {validationErrors.slice(0, 5).map((error, index) => (
              <Typography key={index} variant="body2">
                Row {error.row}: {error.message}
              </Typography>
            ))}
            {validationErrors.length > 5 && (
              <Typography variant="body2">
                ... and {validationErrors.length - 5} more errors
              </Typography>
            )}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showAdvancedOptions}
                  onChange={(e) => setShowAdvancedOptions(e.target.checked)}
                />
              }
              label="Advanced Options"
            />
            {csvData.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                Max {MAX_SCHOOLS_PER_JOB} schools per job
              </Typography>
            )}
          </Box>
          
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={submitBulkJob}
            disabled={csvData.length === 0 || validationErrors.length > 0 || csvData.length > MAX_SCHOOLS_PER_JOB || isSubmitting}
            size="large"
          >
            {isSubmitting ? 'Creating Job...' : `Start Processing ${csvData.length} Schools`}
          </Button>
        </Box>

        {showAdvancedOptions && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Advanced Options
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
              <TextField
                type="number"
                label="Chunk Size"
                value={chunkSize}
                onChange={(e) => setChunkSize(Math.min(5, Math.max(1, parseInt(e.target.value) || 1)))}
                inputProps={{ min: 1, max: 5 }}
                helperText="Schools per batch (1-5)"
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={forceReprocess}
                    onChange={(e) => setForceReprocess(e.target.checked)}
                  />
                }
                label="Force Reprocess"
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createTeamsIfMissing}
                    onChange={(e) => setCreateTeamsIfMissing(e.target.checked)}
                  />
                }
                label="Create Missing Teams"
              />
              {jobType === 'standard_processing' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={runAiSummaries}
                      onChange={(e) => setRunAiSummaries(e.target.checked)}
                    />
                  }
                  label="Run AI Summaries"
                />
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderJobProgress = () => {
    if (!jobStatus) {
      return (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📊 Job Progress
            </Typography>
            <Typography color="text.secondary">
              No active job. Create a job in the Upload tab to see progress here.
            </Typography>
          </CardContent>
        </Card>
      );
    }

    const { progress, chunks, status } = jobStatus;
    const completionPercentage = progress.total_schools > 0 
      ? Math.round((progress.completed / progress.total_schools) * 100) 
      : 0;

    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              📊 Job Progress: {jobStatus.job_id}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                label={status.toUpperCase()}
                color={JOB_STATUS_COLORS[status]}
                variant="filled"
              />
              {['created', 'processing'].includes(status) && (
                <IconButton onClick={cancelJob} color="error" size="small">
                  <CancelIcon />
                </IconButton>
              )}
              {status === 'completed' && progress.failed > 0 && (
                <IconButton onClick={retryFailedSchools} color="warning" size="small">
                  <RetryIcon />
                </IconButton>
              )}
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">
                Overall Progress: {progress.completed} / {progress.total_schools} schools
              </Typography>
              <Typography variant="body2">
                {completionPercentage}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={completionPercentage} 
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mb: 3 }}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
              <Typography variant="h6" color="success.contrastText">
                {progress.completed}
              </Typography>
              <Typography variant="body2" color="success.contrastText">
                Completed
              </Typography>
            </Box>
            
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
              <Typography variant="h6" color="error.contrastText">
                {progress.failed}
              </Typography>
              <Typography variant="body2" color="error.contrastText">
                Failed
              </Typography>
            </Box>
            
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
              <Typography variant="h6" color="primary.contrastText">
                {progress.in_progress}
              </Typography>
              <Typography variant="body2" color="primary.contrastText">
                In Progress
              </Typography>
            </Box>
            
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.300', borderRadius: 1 }}>
              <Typography variant="h6">
                {progress.queued}
              </Typography>
              <Typography variant="body2">
                Queued
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Chunks Progress: {chunks.completed_chunks} / {chunks.total_chunks}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={chunks.total_chunks > 0 ? (chunks.completed_chunks / chunks.total_chunks) * 100 : 0}
              color="secondary"
              sx={{ height: 6, borderRadius: 1 }}
            />
          </Box>

          {jobStatus.estimated_completion && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Estimated completion: {new Date(jobStatus.estimated_completion).toLocaleString()}
              </Typography>
            </Alert>
          )}

          {jobStatus.validation_errors && jobStatus.validation_errors.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Validation Errors ({jobStatus.validation_errors.length}):
              </Typography>
              {jobStatus.validation_errors.slice(0, 3).map((error, index) => (
                <Typography key={index} variant="body2">
                  School {error.index + 1}: {error.error}
                </Typography>
              ))}
              {jobStatus.validation_errors.length > 3 && (
                <Typography variant="body2">
                  ... and {jobStatus.validation_errors.length - 3} more errors
                </Typography>
              )}
            </Alert>
          )}

          {jobStatus.failed_schools && jobStatus.failed_schools.length > 0 && (
            <Alert severity="error">
              <Typography variant="subtitle2" gutterBottom>
                Failed Schools ({jobStatus.failed_schools.length}):
              </Typography>
              {jobStatus.failed_schools.slice(0, 3).map((failed, index) => (
                <Typography key={index} variant="body2">
                  {failed.school_data?.nces_id || failed.school_data?.name || 'Unknown'}: {failed.error}
                </Typography>
              ))}
              {jobStatus.failed_schools.length > 3 && (
                <Typography variant="body2">
                  ... and {jobStatus.failed_schools.length - 3} more failures
                </Typography>
              )}
            </Alert>
          )}

          {jobStatus.ai_summaries_job_id && (
            <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                AI Summaries (separate step)
              </Typography>
              {aiSummariesJobStatus ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">
                      {aiSummariesJobStatus.progress?.completed ?? 0} / {aiSummariesJobStatus.progress?.total_schools ?? 0} schools
                    </Typography>
                    <Chip
                      label={aiSummariesJobStatus.status?.toUpperCase()}
                      color={JOB_STATUS_COLORS[aiSummariesJobStatus.status] || 'default'}
                      size="small"
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={aiSummariesJobStatus.progress?.total_schools > 0
                      ? Math.round((aiSummariesJobStatus.progress.completed / aiSummariesJobStatus.progress.total_schools) * 100)
                      : 0}
                    sx={{ height: 6, borderRadius: 1 }}
                    color="secondary"
                  />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Starting AI summaries…
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        🚀 Bulk Processing
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Process multiple schools in the background with chunked processing, 
        real-time progress tracking, and automatic retry handling.
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Upload & Configure" icon={<UploadIcon />} />
          <Tab label="Monitor Progress" icon={<RefreshIcon />} />
        </Tabs>
      </Box>

      {tabValue === 0 && renderUploadSection()}
      {tabValue === 1 && renderJobProgress()}
    </Box>
  );
};

export default BulkProcessingWorkflow;
